<script>
  import { dndzone, SHADOW_ITEM_MARKER_PROPERTY_NAME } from "svelte-dnd-action"
  import { flip } from "svelte/animate"
  import Fa from "svelte-fa"
  import { faTrashCan } from "@fortawesome/free-regular-svg-icons"
  import { faListOl, faGripVertical } from "@fortawesome/free-solid-svg-icons"
  import { generateId } from "../utils.js"

  export let instructions

  const flipDurationMs = 200
  const dropTargetStyle = {}
  const dragHandleClass = "drag-handle"

  // Use UUIDs for globally unique IDs (needed when items move between groups)
  instructions = instructions.map((group) => {
    return {
      ...group,
      id: group.id || generateId(),
      steps: group.steps.map((step) => {
        return {
          ...step,
          id: step.id || generateId(),
          ref: undefined
        }
      })
    }
  })

  function addGroup() {
    const newGroup = {
      id: generateId(),
      name: "Group name",
      steps: []
    }

    instructions.push(newGroup)
    instructions = instructions
  }

  function deleteGroup(id) {
    instructions = instructions.filter((group) => group.id != id)
  }

  function deleteStep(groupId, id) {
    let group = instructions.find((group) => group.id == groupId)

    group.steps = group.steps.filter((step) => step.id != id)

    instructions = instructions
  }

  function addStep(groupId, event) {
    if (event.key == "Enter" && event.target.value.length > 0) {
      let group = instructions.find((group) => group.id == groupId)

      const step = {
        id: generateId(),
        step: event.target.value
      }

      event.target.value = ""
      group.steps.push(step)
      instructions = instructions
    }
  }

  function onEditStep(event, groupId, i) {
    let group = instructions.find((group) => group.id == groupId)

    if (event.key == "Enter") {
      event.preventDefault()

      const newStep = {
        id: generateId(),
        step: ""
      }

      group.steps.splice(i + 1, 0, newStep)
      instructions = instructions
    }
  }

  // Drag-and-drop handlers for sections (groups)
  function handleSectionDnd(e) {
    instructions = e.detail.items
  }

  // Drag-and-drop handlers for steps within a section
  function handleStepDnd(e, groupId) {
    const group = instructions.find((g) => g.id === groupId)
    if (group) {
      group.steps = e.detail.items
      instructions = instructions
    }
  }
</script>

<main class="flex flex-col gap-4">
  <div
    class="flex flex-row border-b border-dashed border-slate pb-2 items-center"
  >
    <div class="grow text-lg font-light thick">Instructions</div>

    <a class="link text-xs" href="#!" on:click={() => addGroup()}>+ group</a>
  </div>

  <div
    class="flex flex-col gap-4 dnd-zone"
    use:dndzone={{
      items: instructions,
      flipDurationMs,
      type: "sections",
      dropTargetStyle,
      dragHandleClass
    }}
    on:consider={handleSectionDnd}
    on:finalize={handleSectionDnd}
  >
    {#each instructions as group (group.id)}
      <div
        class="border-l-4 border-primary pl-4"
        class:dnd-shadow={group[SHADOW_ITEM_MARKER_PROPERTY_NAME]}
        animate:flip={{ duration: flipDurationMs }}
      >
        {#if instructions.length > 1}
          <div class="mb-2 flex flex-row items-center group relative gap-1">
            <div class="mr-0">
              <Fa icon={faListOl} />
            </div>

            <input
              bind:value={group.name}
              type="text"
              class="font-body font-bold thick text-lg grow !ml-1 focus:!ml-0 "
              placeholder="Enter group name"
            />

            <button
              type="button"
              class="opacity-0 group-hover:opacity-100 p-2 text-xs cursor-pointer text-danger hover:text-danger-hover"
              on:click={() => deleteGroup(group.id)}
            >
              <Fa icon={faTrashCan} />
            </button>

            <button
              type="button"
              class="drag-handle opacity-0 group-hover:opacity-100 cursor-grab p-2 text-gray-500 hover:text-gray-600 text-sm"
              aria-label="Drag to reorder section"
            >
              <Fa icon={faGripVertical} />
            </button>
          </div>
        {/if}

        <div
          class="flex flex-col gap-1 dnd-zone"
          use:dndzone={{
            items: group.steps,
            flipDurationMs,
            type: "steps",
            dropTargetStyle,
            dragHandleClass
          }}
          on:consider={(e) => handleStepDnd(e, group.id)}
          on:finalize={(e) => handleStepDnd(e, group.id)}
        >
          {#each group.steps as step, i (step.id)}
            <div
              class="flex flex-row gap-1 group items-start relative"
              class:dnd-shadow={step[SHADOW_ITEM_MARKER_PROPERTY_NAME]}
              animate:flip={{ duration: flipDurationMs }}
            >
              <div class="pt-0 md:pt-1 md:mr-1 text-base md:text-sm">
                {i + 1}.
              </div>

              <div
                contenteditable
                class="step-input w-full outline-none"
                bind:innerHTML={step.step}
                bind:this={step.ref}
                on:keydown={(event) => onEditStep(event, group.id, i)}
              />

              <button
                type="button"
                class="opacity-0 group-hover:opacity-100 px-1 py-2 text-xs cursor-pointer text-danger hover:text-danger-hover"
                on:click={() => deleteStep(group.id, step.id)}
              >
                <Fa icon={faTrashCan} />
              </button>

              <button
                type="button"
                class="drag-handle opacity-0 group-hover:opacity-100 cursor-grab px-1 py-2 text-gray-500 hover:text-gray-600"
                aria-label="Drag to reorder step"
              >
                <Fa icon={faGripVertical} size="xs" />
              </button>
            </div>
          {/each}
        </div>

        <div class="mt-1 font-light">
          <input
            type="text"
            placeholder="+ step"
            on:keydown={(event) => addStep(group.id, event)}
          />
        </div>
      </div>
    {/each}
  </div>
</main>

<style>
  input[type="text"],
  .step-input {
    @apply border text-lg md:text-xl border-transparent bg-transparent appearance-none rounded w-full py-0 px-0 text-fg leading-tight cursor-pointer;
    @apply md:text-base md:py-0;
    @apply font-body;
    @apply focus:bg-white focus:shadow focus:border focus:outline-none;
    @apply focus:-m-1 focus:px-1 focus:py-1;
  }

  /* Remove default dndzone outline */
  .dnd-zone {
    outline: none !important;
  }

  /* Shadow placeholder for drop position */
  .dnd-shadow {
    @apply bg-gray-100 border border-dashed border-gray-400 rounded;
    visibility: visible !important;
  }

  .dnd-shadow * {
    visibility: hidden;
  }
</style>
